import React, { forwardRef, useImperativeHandle, RefAttributes } from "react";
import { useODataContext } from "../contexts/ODataContext";
import {
  Grid,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridToolbar,
  GridSelectionChangeEvent,
  getSelectedState,
  GridHeaderSelectionChangeEvent,
  GridColumnProps,
  GridRowClickEvent
} from "@progress/kendo-react-grid";
import { isCompositeFilterDescriptor, State } from "@progress/kendo-data-query";
import { getter } from "@progress/kendo-react-common";
import { filterIcon } from "@progress/kendo-svg-icons";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import config from "../contexts/ConfigContext";
import { Button } from "@progress/kendo-react-buttons";
import { Trans } from 'react-i18next';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { t } from "i18next";
import { PermissionButton } from "./PermissionButton";


export interface ColumnCustomProps extends GridColumnProps {
  isExportable?: boolean;
}


interface DataGridProps extends RefAttributes<DataGridHandle> {
  Columns: ColumnCustomProps[];
  DataItemKey: string;
  onSelectedItemsChange?: (selectedItems: number[]) => void;
  Height?: string;
  ShowAddButton?: boolean;
  ShowRemoveButton?: boolean;
  onAddButtonClick?: () => void;
  onRemoveButtonClick?: () => void;
  PermissionButtonCode?: string;
  PageSize?: number;
  onRowClick?:(event: GridRowClickEvent) => void;
  autoBind?: boolean;
  editField?: string;
  onDataStateChange?: () => void;
  ShowCustomButton?: boolean;
  onCustomButtonClick?: () => void;
  CustomButtonLabel?: string;
  enableCustomButton?: boolean;
  enablePagination?: boolean;
}

export type DataGridHandle = {
  refreshData: () => void;
  refreshDataFromFirstPage: () => void;
  filterData: (filter?: CompositeFilterDescriptor) => void;
  orderData: (sort: any) => void;
  editInline: (id: number) => void;
  cancelEditInline: (id: number) => void;
};

const DataGridCustom: React.FC<DataGridProps> = forwardRef<DataGridHandle, DataGridProps>((props, ref) => {
  // Define properties and hooks
  const SELECTED_FIELD: string = "selected";
  const { data, update, total, loadData } = useODataContext();
  const pageSize = props.PageSize ? props.PageSize : config.PAGE_SIZE;
  const [dataState, setDataState] = React.useState<State>({ take: pageSize, skip: 0, });
  const height = props.Height ? props.Height : "100%";
  const autoBindSet = props.autoBind !== undefined ? props.autoBind : true;
  const pagination = props.enablePagination !== undefined ? props.enablePagination : true;
  const {editField} = props;
  const idGetter = getter(props.DataItemKey);

  // Load data on component mount
  React.useEffect(() => {
    if(autoBindSet){
      loadData(dataState);
    }
  }, []);

  // Handle data state change
  const dataStateChange = (e: GridDataStateChangeEvent) => {
    if(e.dataState.filter){
      filterFixed(e.dataState.filter);
    }
  
    setDataState(e.dataState);
    loadData(e.dataState);
    // Call onDataStateChange event for handling data state change in parent component- this is useful for cancelling inline editing after data state change
    if (props.onDataStateChange) {
      props.onDataStateChange();
    }
  };

  const refreshData = () => {
    const newState = { ...dataState, skip: 0 };
    setDataState(newState);
    loadData(newState);
  }

  // Reset pagination
  const refreshDataFromFirstPage = () => {
    let resetState = { ...dataState, skip: 0 };
    setDataState(resetState);
    loadData(resetState);
  }

  const filterFixed = (filter: CompositeFilterDescriptor) => {
    if (filter) {
      filter.filters.forEach(function (filter, index, arr) {
        // Check if there multiple filters
        if (isCompositeFilterDescriptor(filter)) {
            filterFixed(filter);
        } else {
            // This filter needs to be case-insensitive
            if (filter.operator === "contains" || filter.operator === "startswith" || filter.operator === "doesnotcontain" || filter.operator === "endswith" || filter.operator === "eq" || filter.operator === "neq") {
                // This property change the field in lowerCase field
                filter.ignoreCase = true;
                // Change value to lowerCase
                if (typeof (filter.value) === "string") {
                    filter.value = filter.value.toLowerCase();
                }
            }
        }
    })
    }
  }

  const filterData = (filter?: CompositeFilterDescriptor) => {
    if (filter) {
        filterFixed(filter);
    }
    // Use functional setState to merge over the latest state and keep the current sort
    setDataState((prev) => {
      const newState = { ...prev, filter, skip: 0 };
      loadData(newState);
      return newState;
    });
  }

  const orderData = (sort: any) => {
    // Use functional setState to merge over the latest state and keep the current filter
    setDataState((prev) => {
      const newState = { ...prev, sort, skip: 0 };
      loadData(newState);
      return newState;
    });
  }

  const editInline =(id:number) => {
    let allData = data.map((item: any) =>
        item.AnnotationId == id
          ? {
              ...item,
              inEdit: true,
            }
          : item
      );
    
    update(allData);
    
  }

  const cancelEditInline = (id:number) => {
    let allData = data.map((item: any) =>
      item.AnnotationId == id
        ? {
            ...item,
            inEdit: false,
          }
        : item
    );
  
    update(allData);
  }

  useImperativeHandle(ref, () => ({
    refreshData, refreshDataFromFirstPage, filterData, orderData, editInline: editInline, cancelEditInline
  }));

  // Export to Excel
  const _export = React.useRef<ExcelExport | null>(null);
  const _grid = React.useRef<any>();
  const excelExport = () => {
    if (_export.current !== null) {
      // export all data to excel      
      loadData({ take: total, skip: 0 }).then((items) => {
        saveExcel(items);
      });

    }
  };

  const saveExcel = (items: any) => {
    if (_export.current !== null) {
      _export.current.save(items);
      loadData(dataState);
    }
  }

  // Handle selection change
  const [selectedState, setSelectedState] = React.useState<{
    [id: string]: boolean | number[];
  }>({});

  // Handle selection change
  const onSelectionChange = React.useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: props.DataItemKey,
      });
      setSelectedState(newSelectedState);     
      if(props.onSelectedItemsChange)
        props.onSelectedItemsChange(Object.keys(newSelectedState).map(Number).filter((item) => newSelectedState[item] === true));
    },
    [selectedState]
  );

  // Handle header selection change
  const onHeaderSelectionChange = React.useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState: { [key: string]: boolean } = {};

      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
      if(props.onSelectedItemsChange)
        props.onSelectedItemsChange(Object.keys(newSelectedState).map(Number).filter((item) => newSelectedState[item] === checked));
    },
    []
  );

  return (
    <div>
      <ExcelExport ref={_export}>
        {props.Columns.filter(c => c.isExportable !== undefined && c.isExportable === true).map((column, index) => {
          return <ExcelExportColumn key={index} field={column.field} title={column.title} />
        })}
      </ExcelExport>
      <Grid        
        ref={_grid}
        data={{
          data: data.map((item) => ({
            ...item,
            [SELECTED_FIELD]: selectedState[idGetter(item)],
          })), total: total
        }}
        {...dataState}
        total={total}
        style={{ maxHeight: height }}
        sortable={true}
        pageable={pagination}
        dataItemKey={props.DataItemKey}
        selectedField={SELECTED_FIELD}
        pageSize={pageSize}
        columnMenuIcon={filterIcon}
        selectable={{
          enabled: true,
          drag: false,
          cell: false,
          mode: "multiple",
        }}
        onDataStateChange={dataStateChange}
        onSelectionChange={onSelectionChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
        onRowClick={props.onRowClick}
        editField={editField}
      >
        <GridToolbar>
          <Button
            title="Export Excel"
            themeColor={"secondary"}       
            type="button"
            onClick={excelExport}
          >           
            <Trans>general.exportToExcel</Trans>
          </Button>
          { props.ShowAddButton && 
            <PermissionButton code={props.PermissionButtonCode} mode="create">
              <Button title={t("general.add")} themeColor={"primary"} type="button" onClick={props.onAddButtonClick}><Trans>general.add</Trans></Button>
            </PermissionButton>
          }
          { props.ShowRemoveButton &&     
           <PermissionButton code={props.PermissionButtonCode} mode="edit">
              <Button title={t("general.remove")} themeColor={"error"} type="button" onClick={props.onRemoveButtonClick}><Trans>general.remove</Trans></Button>
           </PermissionButton>
          }
          {props.ShowCustomButton &&
             <Button title={t(props.CustomButtonLabel??"")} themeColor={"primary"} type="button" disabled={!props.enableCustomButton} onClick={props.onCustomButtonClick}><Trans>{props.CustomButtonLabel}</Trans></Button>
          }
        </GridToolbar>
        {(props.onSelectedItemsChange) &&
        <Column
          field={SELECTED_FIELD}
          width="65px"
          headerSelectionValue={
            data.findIndex((item) => !selectedState[idGetter(item)]) === -1
          }
        />
    }
        {props.Columns.map((column, index) => {
          return <Column key={index} {...column} />
        })}

      </Grid>
    </div>
  );

});

export default DataGridCustom;